import { useState, useEffect, useRef } from 'react';

// --- Parallax Hook ---
function useParallax(speed = 0.3) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleScroll = () => {
      const rect = el.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translateY(${centerY * speed}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);
  return ref;
}

// --- Box Parallax Hook ---
function useBoxParallax(speed = 0.05) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleScroll = () => {
      const parent = el.closest('.about-image-grid') as HTMLElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translateY(${centerY * speed}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);
  return ref;
}

// --- Scroll Reveal Hook ---
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('reveal-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [heroIndex, setHeroIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuBgRef = useParallax(0.25);
  const combosBgRef = useParallax(0.2);
  const aboutBgRef = useParallax(0.25);
  const contactBgRef = useParallax(0.2);
  const testimonialsBgRef = useParallax(0.15);

  const menuHeadingRef = useScrollReveal();
  const guisadosRef = useScrollReveal();
  const handHeldsRef = useScrollReveal();
  const sidesRef = useScrollReveal();
  const drinksRef = useScrollReveal();
  const combosRef = useScrollReveal();
  const tacoSpecialRef = useScrollReveal();
  const weekendRef = useScrollReveal();
  const aboutRef = useScrollReveal();
  const testimonialsRef = useScrollReveal();

  const aboutBox1Ref = useBoxParallax(-0.06);
  const aboutBox2Ref = useBoxParallax(0.08);
  const aboutBox3Ref = useBoxParallax(0.04);
  const aboutBox4Ref = useBoxParallax(-0.10);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex(prev => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = ['home', 'menu', 'combos', 'about', 'contact', 'catering'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 72;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const navSections = ['home', 'menu', 'combos', 'about', 'contact', 'catering'];
  const navLabels: Record<string, string> = { home: 'Home', menu: 'Menu', combos: 'Combos', about: 'About', contact: 'Contact', catering: 'Catering' };

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .reveal-item {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .reveal-visible .reveal-item,
        .reveal-visible.reveal-item {
          opacity: 1;
          transform: translateY(0);
        }
        .reveal-stagger .reveal-item:nth-child(1) { transition-delay: 0s; }
        .reveal-stagger .reveal-item:nth-child(2) { transition-delay: 0.1s; }
        .reveal-stagger .reveal-item:nth-child(3) { transition-delay: 0.2s; }
        .reveal-stagger .reveal-item:nth-child(4) { transition-delay: 0.3s; }
        .reveal-stagger .reveal-item:nth-child(5) { transition-delay: 0.4s; }
        .reveal-stagger .reveal-item:nth-child(6) { transition-delay: 0.5s; }

        @keyframes kenBurns {
          0%   { transform: scale(1)    translateX(0%)   translateY(0%); }
          50%  { transform: scale(1.08) translateX(-1%)  translateY(-1%); }
          100% { transform: scale(1)    translateX(0%)   translateY(0%); }
        }
        .ken-burns { animation: kenBurns 18s ease-in-out infinite; transform-origin: center center; }

        @keyframes kenBurns2 {
          0%   { transform: scale(1.06) translateX(1%)  translateY(0%); }
          50%  { transform: scale(1)    translateX(-1%) translateY(-1%); }
          100% { transform: scale(1.06) translateX(1%)  translateY(0%); }
        }
        .ken-burns-2 { animation: kenBurns2 20s ease-in-out infinite; transform-origin: center center; }

        @keyframes kenBurns3 {
          0%   { transform: scale(1.04) translateX(-1%) translateY(1%); }
          50%  { transform: scale(1.1)  translateX(1%)  translateY(-1%); }
          100% { transform: scale(1.04) translateX(-1%) translateY(1%); }
        }
        .ken-burns-3 { animation: kenBurns3 22s ease-in-out infinite; transform-origin: center center; }

        @keyframes kenBurns4 {
          0%   { transform: scale(1.08) translateX(0%)  translateY(-1%); }
          50%  { transform: scale(1.05) translateX(-1%) translateY(1%); }
          100% { transform: scale(1.08) translateX(0%)  translateY(-1%); }
        }
        .ken-burns-4 { animation: kenBurns4 19s ease-in-out infinite; transform-origin: center center; }

        .hero-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 1.8s ease-in-out;
        }
        .hero-slide.active { opacity: 1; }

        @keyframes panRight {
          0%   { transform: scale(1.08) translateX(-2%); }
          100% { transform: scale(1.08) translateX(2%); }
        }
        .pan-right { animation: panRight 20s ease-in-out infinite alternate; }

        @keyframes floatBadge {
          0%   { transform: translateY(0px); }
          50%  { transform: translateY(-14px); }
          100% { transform: translateY(0px); }
        }
        .float-badge { animation: floatBadge 3.5s ease-in-out infinite; }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mobile-menu-open { animation: slideDown 0.25s ease forwards; }
      `}</style>

      {/* ═══ NAVIGATION ═══ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#33241c]/95 backdrop-blur-sm border-b-4 border-[#ed262b]' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
          <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/d20479ae32bf411ee3def3c20c31d00e.png" alt="Aquí se Come" className="h-10 md:h-14" />
          <div className="hidden md:flex items-center gap-8">
            {navSections.map((section) => (
              <button key={section} onClick={() => scrollToSection(section)} className={`text-sm uppercase tracking-wide transition-all duration-300 hover:text-[#ed262b] relative whitespace-nowrap cursor-pointer font-rubikMono ${activeSection === section ? 'text-[#ed262b]' : 'text-[#FFF8DC]'}`}>
                {navLabels[section]}
                {activeSection === section && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#ed262b]"></span>}
              </button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:469-716-7232" className="text-[#FFF8DC] hover:text-[#ed262b] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill text-lg"></i>
              <span className="text-sm font-figtree font-medium">469.716.7232</span>
            </a>
            <div className="flex items-center gap-3 ml-4">
              <a href="https://www.facebook.com/p/Aqui-Se-Come-61585768157603/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a href="https://www.instagram.com/aquisecome817/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                <i className="ri-instagram-fill text-lg"></i>
              </a>
            </div>
          </div>
          <div className="flex md:hidden items-center gap-3">
            <a href="tel:469-716-7232" className="text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
              <i className="ri-phone-fill text-xl"></i>
            </a>
            <button onClick={() => setMobileMenuOpen(prev => !prev)} className="w-10 h-10 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
              <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden mobile-menu-open bg-[#33241c]/97 backdrop-blur-sm border-t border-[#ed262b]/30 px-4 py-4">
            <div className="space-y-1">
              {navSections.map((section) => (
                <button key={section} onClick={() => scrollToSection(section)} className={`w-full text-left px-4 py-3 rounded-xl text-sm uppercase tracking-wide font-rubikMono transition-all cursor-pointer ${activeSection === section ? 'text-[#ed262b] bg-[#ed262b]/10' : 'text-[#FFF8DC] hover:text-[#ed262b] hover:bg-[#ed262b]/10'}`}>
                  {navLabels[section]}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#ed262b]/20 px-4">
              <a href="https://www.facebook.com/p/Aqui-Se-Come-61585768157603/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="https://www.instagram.com/aquisecome817/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                <i className="ri-instagram-fill text-xl"></i>
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ═══ HERO SECTION ═══ */}
      <section id="home" className="relative h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className={`hero-slide${heroIndex === 0 ? ' active' : ''}`}>
            <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/2bbf13acaf600b0bfcc8d9b5aa87ab3e.png" alt="Sizzling Tacos" className="w-full h-full object-cover object-center ken-burns" />
          </div>
          <div className={`hero-slide${heroIndex === 1 ? ' active' : ''}`}>
            <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/787e6b77645cfae00c3e04d43a63a9e1.png" alt="Street Tacos" className="w-full h-full object-cover object-center ken-burns-2" />
          </div>
          <div className={`hero-slide${heroIndex === 2 ? ' active' : ''}`}>
            <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/fb6eefc90a4e84ddb30a13c99e86e6af.png" alt="Torta & Fries" className="w-full h-full object-cover object-center ken-burns-3" />
          </div>
          <div className={`hero-slide${heroIndex === 3 ? ' active' : ''}`}>
            <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/3aa56ea5062aa63bca918e74f71da5f2.png" alt="Mexican Food Spread" className="w-full h-full object-cover object-center ken-burns-4" />
          </div>
        </div>
        <div className="absolute inset-0 bg-black/30 md:bg-black/20"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center text-center px-5 md:px-6 pt-20 pb-8 md:justify-center md:pt-0 md:pb-0">
          <div className="flex-1 md:hidden"></div>
          <div className="flex flex-col items-center text-center">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-rubik font-extrabold text-white mb-3 md:mb-4 tracking-wide leading-tight">FLAVOR STARTS HERE</h1>
            <p className="text-[15pt] font-figtree font-medium text-white mb-6 md:mb-8 tracking-wide">Arlington&apos;s hidden gem for bold,<br />slow-cooked Mexican flavor.</p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto">
              <button onClick={() => scrollToSection('menu')} className="bg-white text-[#ed262b] px-6 md:px-8 py-3.5 md:py-4 rounded-full text-sm md:text-base font-rubikMono font-semibold hover:bg-[#ed262b] hover:text-white transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer">
                View Menu
              </button>
              <button onClick={() => scrollToSection('catering')} className="border-2 border-white text-white px-6 md:px-8 py-3.5 md:py-4 rounded-full text-sm md:text-base font-rubikMono font-semibold hover:bg-[#FFF8DC] hover:text-[#ed262b] hover:border-[#FFF8DC] transition-all duration-300 whitespace-nowrap cursor-pointer">
                Order Catering
              </button>
            </div>
            <div className="flex gap-2 mt-8 md:mt-10">
              {[0, 1, 2, 3].map(i => (
                <button key={i} onClick={() => setHeroIndex(i)} className={`rounded-full transition-all duration-300 cursor-pointer ${heroIndex === i ? 'w-8 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`} />
              ))}
            </div>
          </div>
          <div className="flex-1 md:hidden flex items-center justify-center">
            <div className="w-24 h-24 float-badge md:hidden">
              <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/b8279c0fc048b89ab757426e0290d14c.png" alt="Aquí se Come" className="w-full h-full object-contain drop-shadow-2xl" />
            </div>
          </div>
        </div>
        <div className="hidden md:block absolute md:bottom-12 md:right-12 md:w-44 md:h-44">
          <div className="w-full h-full float-badge">
            <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/b8279c0fc048b89ab757426e0290d14c.png" alt="Aquí se Come" className="w-full h-full object-contain drop-shadow-2xl" />
          </div>
        </div>
      </section>

      {/* ═══ MENU SECTION ═══ */}
      <section id="menu" className="py-14 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div ref={menuBgRef} className="absolute inset-[-15%]">
            <img src="https://public.readdy.ai/ai/img_res/ddd0e9fc35fa4535f3cbba9c3db33abd.jpg" alt="Mexican Pattern" className="w-full h-full object-cover opacity-10" />
          </div>
        </div>
        <div className="absolute inset-0 bg-white/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div ref={menuHeadingRef} className="reveal-item mb-10 md:mb-16">
            <h2 className="text-5xl md:text-8xl font-rubik font-extrabold text-[#33241c] mb-2">MENÚ</h2>
          </div>

          {/* Guisados Grid */}
          <div ref={guisadosRef} className="mb-14 md:mb-20 reveal-stagger">
            <h3 className="text-3xl md:text-5xl font-rubik font-extrabold text-[#ed262b] mb-8 md:mb-12 reveal-item">GUISADOS</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {[
                { name: 'Carnitas', desc: 'Slow-cooked, seasoned pork shredded or pulled apart.', customImg: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/aba9a2df-7b90-4c9d-a9fc-82122f110ed5_guisados_0002_3.png?v=37b3c71c4711d1fe8f6ff81d55d3fe02', height: 'h-72 md:h-96' },
                { name: 'Barbacoa', desc: 'Tender, slow-cooked beef, rich in flavor.', customImg: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/d04173fc-7b4d-4deb-8185-84df8db47360_guisados_0001_2.png?v=5acfab96757fe700222db0eb97122f3c', height: 'h-72 md:h-[420px]' },
                { name: 'Chicken Tinga', desc: 'Shredded chicken in a smoky, spicy tomato sauce.', customImg: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/803d386d-c738-46e1-b556-fab452738fdc_guisados_0003_4.png?v=6e1d05b9f3ab4706323538165817df92', height: 'h-72 md:h-96' },
                { name: 'Fajita Chicken', desc: 'Chicken sautéed with peppers.', customImg: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/344a2849-ff87-4af1-87c7-97e3e66bf1df_guisados_0000_1.png?v=d845e1369166ca0d2001351df3412dd8', height: 'h-72 md:h-[420px]' },
                { name: 'Carne Guisado', desc: 'Stewed meat, tender and delicious.', customImg: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/0617e075-e0e0-4504-9132-728178ec7039_guisados_0004_5.png?v=431fdc0ff0c29e25621c8d0f27125a82', height: 'h-72 md:h-96' },
                { name: 'Chicharrón Guisada', desc: 'Crispy fried pork rinds in a savory sauce.', customImg: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/f6395d62-4069-4c3e-b41f-7faa64e46102_guisados_0005_6.png?v=c936ab3adddf33136f12c5265c506f6e', height: 'h-72 md:h-[420px]' }
              ].map((item, idx) => (
                <div key={idx} className={`${item.height} bg-white rounded-2xl md:rounded-3xl overflow-hidden reveal-item`}>
                  <div className="h-[62%] overflow-hidden relative">
                    <img src={item.customImg} alt={item.name} className="w-full h-full object-cover object-top transition-transform duration-[8000ms] ease-in-out hover:scale-105" />
                  </div>
                  <div className="p-4 md:p-5 h-[38%] flex flex-col justify-center bg-[#FDF8EE]">
                    <h4 className="text-xl md:text-2xl font-rubik font-extrabold text-[#33241c] mb-1">{item.name}</h4>
                    <p className="text-xs md:text-sm font-figtree font-normal text-[#33241c] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hand-Helds */}
          <div ref={handHeldsRef} className="space-y-4 md:space-y-8 reveal-stagger">
            <h3 className="text-3xl md:text-5xl font-rubik font-extrabold text-[#ed262b] mb-6 md:mb-8 reveal-item">HAND-HELDS</h3>
            {[
              { category: 'PLATE', price: '$12', desc: '3 tacos on corn or flour tortillas. Choice of meat served with rice and fried beans.', includes: ['Grilled onions', 'Jalapeños', 'Cilantro', 'Lime', 'Red or green salsa'] },
              { category: 'QUESADILLA', price: '$11', desc: 'Melted cheese with your choice of meat on a flour tortilla.', includes: ['Lettuce', 'Pico de gallo', 'Sour cream'] },
              { category: 'TACO', price: '$3.25', desc: 'Your choice of meat on corn or flour tortilla.', includes: ['Grilled onions', 'Grilled jalapeños', 'Lime', 'Cilantro', 'Onion', 'Red or green salsa'] },
              { category: 'TORTA', price: '$12', desc: 'Your choice of meat with cheese, lettuce, tomatoes. Served with a side of fries.', includes: ['Grilled onions', 'Grilled jalapeños', 'Red or green salsa'] },
              { category: 'BURRITO', price: '$11', desc: 'Your choice of meat with rice and fried beans on a flour tortilla.', includes: ['Lettuce', 'Tomato', 'Sour cream', 'Cheese', 'Red or green salsa'] },
              { category: 'GORDITA', price: '$4.50', desc: 'Filled with refried beans, your choice of meat.', includes: ['Lettuce', 'Tomato', 'Sour cream', 'Cheese'] }
            ].map((item, idx) => (
              <div key={idx} className="reveal-item">
                <div className="bg-[#FDF8EE] rounded-xl md:rounded-2xl p-4 md:p-6">
                  <div className="flex justify-between items-start gap-3 mb-3 md:mb-4">
                    <h4 className="text-lg md:text-2xl font-rubik font-extrabold text-[#33241c]">{item.category}</h4>
                    <div className="text-2xl md:text-4xl font-rubik font-extrabold text-[#ed262b] shrink-0">{item.price}</div>
                  </div>
                  <p className="text-xs md:text-sm font-figtree font-normal text-[#33241c] leading-relaxed mb-3">{item.desc}</p>
                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {item.includes.map((inc, i) => (
                      <span key={i} className="bg-transparent text-[#33241c] text-xs px-2.5 py-1 rounded-full font-figtree font-medium" style={{ border: '0.5px solid #33241c' }}>{inc}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sides */}
          <div ref={sidesRef} className="mt-12 md:mt-16 reveal-stagger">
            <h3 className="text-3xl md:text-5xl font-rubik font-extrabold text-[#ed262b] mb-6 md:mb-8 reveal-item">SIDES</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {[
                { name: 'French Fries', price: '$3' },
                { name: 'Refried Beans', price: '$3' },
                { name: 'Rice', price: '$3' },
                { name: 'Tortillas', price: '$2', note: '4 pieces' }
              ].map((side, idx) => (
                <div key={idx} className="bg-[#FDF8EE] rounded-xl md:rounded-2xl p-4 md:p-6 text-center reveal-item">
                  <h4 className="text-base md:text-xl font-rubik font-extrabold text-[#33241c] mb-1">{side.name}</h4>
                  <p className="text-2xl md:text-3xl font-rubik font-extrabold text-[#ed262b]">{side.price}</p>
                  {side.note && <p className="text-xs font-figtree font-normal text-[#33241c] mt-1">{side.note}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Drinks */}
          <div ref={drinksRef} className="mt-10 md:mt-16 reveal-item">
            <h3 className="text-3xl md:text-5xl font-rubik font-extrabold text-[#ed262b] mb-6 md:mb-8">DRINKS</h3>
            <div className="bg-[#FDF8EE] rounded-xl md:rounded-2xl p-5 md:p-8">
              <div className="flex justify-between items-center gap-4">
                <div>
                  <h4 className="text-xl md:text-2xl font-rubik font-extrabold text-[#33241c] mb-1">Agua Fresca</h4>
                  <p className="text-xs md:text-sm font-figtree font-normal text-[#33241c] leading-relaxed max-w-xs md:max-w-2xl">A classic Mexican drink made with real fruit, water, and just the right touch of sweetness.</p>
                </div>
                <div className="text-3xl md:text-4xl font-rubik font-extrabold text-[#ed262b] shrink-0">$6</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ COMBOS SECTION ═══ */}
      <section id="combos" className="py-14 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div ref={combosBgRef} className="absolute inset-[-15%]">
            <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/48a5ac47fe572b7b8b7bbc8a2631b525.jpeg" alt="Mexican Art" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div ref={combosRef} className="reveal-stagger">
            <h2 className="text-3xl md:text-6xl font-rubik font-extrabold text-white mb-8 md:mb-12 reveal-item">COMBO AQUÍ SE COME</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12">
              <div className="md:col-span-3 space-y-3 md:space-y-4 reveal-item">
                {[
                  { name: '3 tacos + Agua fresca', price: '$9' },
                  { name: '2 gordita + Agua fresca', price: '$8' },
                  { name: 'Burrito + Agua fresca', price: '$12' },
                  { name: 'Quesadilla + Agua fresca', price: '$12' },
                  { name: 'Torta + fries + Agua fresca', price: '$14' }
                ].map((combo, idx) => (
                  <div key={idx} className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6">
                    <div className="flex justify-between items-center gap-3">
                      <h4 className="text-base md:text-xl font-rubik font-extrabold text-[#33241c]">{combo.name}</h4>
                      <span className="text-2xl md:text-3xl font-rubik font-extrabold text-[#41ad49] shrink-0">{combo.price}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="md:col-span-2 flex items-center justify-center reveal-item">
                <div className="relative w-52 h-52 md:w-72 md:h-72 animate-float">
                  {/* Spinning circle */}
                  <div className="absolute inset-0 rounded-full bg-[#FFF8DC]" />
                  {/* Counter-spinning text stays upright */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-[#41ad49] text-xs md:text-sm font-rubik font-extrabold uppercase tracking-wider mb-1 md:mb-2">Best Value</div>
                      <div className="text-[#33241c] text-xl md:text-2xl font-rubikMono leading-tight">3 TACOS</div>
                      <div className="text-[#33241c] text-xl md:text-2xl font-rubikMono leading-tight mb-2 md:mb-4">+ DRINK</div>
                      <div className="text-[#41ad49] text-5xl md:text-6xl font-rubik font-extrabold">$9</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Taco Special */}
          <div ref={tacoSpecialRef} className="mt-14 md:mt-20 reveal-stagger">
            <h3 className="text-3xl md:text-5xl font-rubik font-extrabold text-white text-center mb-8 md:mb-12 reveal-item">TACO SPECIAL</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
              {[
                { qty: '10', price: '$24' },
                { qty: '25', price: '$60' },
                { qty: '30', price: '$72' }
              ].map((special, idx) => (
                <div key={idx} className="bg-white border-2 border-[#41ad49] rounded-2xl p-6 md:p-8 text-center reveal-item">
                  <div className="text-6xl md:text-8xl font-rubik font-extrabold text-[#41ad49] mb-1 md:mb-2">{special.qty}</div>
                  <div className="text-lg md:text-2xl uppercase font-rubikMono text-[#33241c] mb-2 md:mb-4">TACOS</div>
                  <div className="text-3xl md:text-4xl font-rubik font-extrabold text-[#33241c] mb-2 md:mb-3">{special.price}</div>
                  <p className="text-xs font-figtree font-normal text-[#666]">includes rice &amp; beans</p>
                </div>
              ))}
            </div>
          </div>

          {/* Weekend Specials */}
          <div ref={weekendRef} className="mt-14 md:mt-20 bg-[#2C2C2C] rounded-2xl md:rounded-3xl p-6 md:p-12 relative overflow-hidden reveal-item">
            <div className="absolute inset-0">
              <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/b3b163c5d33caa5536fbc1398e4d1152.png" alt="Wood Texture" className="w-full h-full object-cover opacity-100" />
            </div>
            <div className="relative z-10">
              <h3 className="text-3xl md:text-7xl font-rubik font-extrabold text-white text-center mb-2 md:mb-4 flex items-center justify-center gap-3 md:gap-6">
                <span>★</span><span>WEEKEND SPECIALS</span><span>★</span>
              </h3>
              <p className="text-center text-sm md:text-xl font-rubikMono text-white mb-8 md:mb-12">Saturday &amp; Sundays</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
                {[
                  { name: 'SOUP OF THE DAY', price: '$13' },
                  { name: 'SPECIAL OF THE DAY', price: '$13' }
                ].map((special, idx) => (
                  <div key={idx} className="bg-[#41ad49] rounded-2xl md:rounded-3xl p-8 md:p-12 flex flex-col items-center justify-center text-center border-2 border-white">
                    <h4 className="text-xl md:text-3xl font-rubik font-extrabold text-white mb-3 md:mb-6">{special.name}</h4>
                    <p className="text-4xl md:text-5xl font-rubik font-extrabold text-[#33241c]">{special.price}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ABOUT SECTION ═══ */}
      <section id="about" className="py-14 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div ref={aboutBgRef} className="absolute inset-[-15%]">
            <img src="https://readdy.ai/api/search-image?query=rustic%20mexican%20textured%20wall%20stucco%20terracotta%20orange%20warm%20tones%20aged%20weathered%20traditional%20authentic%20background%20texture&width=1920&height=1080&seq=about-bg-texture&orientation=landscape" alt="Rustic Background" className="w-full h-full object-cover opacity-15" />
          </div>
        </div>
        <div className="absolute inset-0 bg-white/90"></div>
        <div ref={aboutRef} className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 reveal-stagger">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="about-image-grid relative h-[600px] reveal-item hidden md:block">
              <div ref={aboutBox1Ref} className="absolute top-0 left-0 w-96 h-72 rounded-3xl overflow-hidden shadow-xl z-10" style={{ willChange: 'transform' }}>
                <img src="https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/bb0efd66-8ca6-47e1-8807-5d1c86a1514e_800--600.png?v=34ce4c10acd3ecdd3c147a152eb7ee34" alt="Kitchen" className="w-full h-full object-cover pan-right" />
              </div>
              <div ref={aboutBox2Ref} className="absolute top-32 right-0 w-80 h-64 rounded-3xl overflow-hidden shadow-xl z-20" style={{ willChange: 'transform' }}>
                <img src="https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/a0747159-56c9-4850-9eb6-3630c93bb06c_700--560.png?v=8e6e06ec355e18c52876e7929e17b385" alt="Food" className="w-full h-full object-cover pan-right" style={{ animationDelay: '-5s' }} />
              </div>
              <div ref={aboutBox3Ref} className="absolute bottom-20 left-12 w-72 h-56 rounded-3xl overflow-hidden shadow-xl z-10" style={{ willChange: 'transform' }}>
                <img src="https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/ad3ffac2-be6b-499c-b046-e0c1062a2aee_640--480.png?v=a91752d3d2411b4f744fb7d46f787999" alt="Interior" className="w-full h-full object-cover pan-right" style={{ animationDelay: '-10s' }} />
              </div>
              <div ref={aboutBox4Ref} className="absolute bottom-0 right-16 w-64 h-48 rounded-3xl overflow-hidden shadow-xl z-20" style={{ willChange: 'transform' }}>
                <img src="https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/f8b22db2-26fb-4528-af97-2c862a6370b9_560--420.png?v=65a453264211229652e741614f7dd027" alt="Customers" className="w-full h-full object-cover pan-right" style={{ animationDelay: '-15s' }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 reveal-item md:hidden">
              {[
                { src: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/bb0efd66-8ca6-47e1-8807-5d1c86a1514e_800--600.png?v=34ce4c10acd3ecdd3c147a152eb7ee34', alt: 'Kitchen' },
                { src: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/a0747159-56c9-4850-9eb6-3630c93bb06c_700--560.png?v=8e6e06ec355e18c52876e7929e17b385', alt: 'Food' },
                { src: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/ad3ffac2-be6b-499c-b046-e0c1062a2aee_640--480.png?v=a91752d3d2411b4f744fb7d46f787999', alt: 'Interior' },
                { src: 'https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/f8b22db2-26fb-4528-af97-2c862a6370b9_560--420.png?v=65a453264211229652e741614f7dd027', alt: 'Customers' }
              ].map((img, idx) => (
                <div key={idx} className="h-36 rounded-2xl overflow-hidden">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </div>
            <div className="reveal-item">
              <div className="inline-block border-2 border-[#ed262b] text-[#ed262b] text-xs uppercase font-figtree font-black px-4 py-2 rounded-full mb-6 md:mb-8">Our Story</div>
              <h2 className="text-4xl md:text-6xl font-rubik font-extrabold text-[#33241c] mb-4 md:mb-6">Aquí se Come</h2>
              <div className="space-y-3 md:space-y-4 text-sm md:text-base font-figtree font-normal text-[#666] leading-relaxed max-w-md">
                <p>Welcome to Aquí se Come, where authentic Mexican flavors come to life. Located in the heart of Arlington, Texas, we&apos;ve been serving our community with traditional recipes passed down through generations.</p>
                <p>Our guisados are slow-cooked to perfection, using only the finest ingredients and time-honored techniques. From our tender carnitas to our rich barbacoa, every dish tells a story of Mexican culinary heritage.</p>
                <p>Whether you&apos;re joining us for a quick taco or planning a special event with our catering services, we&apos;re committed to bringing you the authentic taste of Mexico with every bite.</p>
              </div>
              <p className="text-sm md:text-base font-rubik font-extrabold text-[#ed262b] mt-6 md:mt-8">Flavor Starts Here.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="py-14 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8EE]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div ref={testimonialsRef} className="reveal-stagger">
            <div className="text-center mb-10 md:mb-16 reveal-item">
              <div className="inline-block bg-[#ed262b] text-white text-xs uppercase font-figtree font-medium px-4 py-2 rounded-full mb-4 md:mb-6">Lo que dicen nuestros clientes</div>
              <h2 className="text-4xl md:text-6xl font-rubik font-extrabold text-[#33241c] mb-4">Testimonios</h2>
              <div className="flex items-center justify-center gap-3">
                <div className="h-0.5 w-16 md:w-24 bg-[#ed262b]"></div>
                <i className="ri-heart-fill text-xl md:text-2xl text-[#ed262b]"></i>
                <div className="h-0.5 w-16 md:w-24 bg-[#ed262b]"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
              {[
                { name: 'María González', location: 'Arlington, TX', rating: 5, text: '¡Las mejores carnitas que he probado fuera de México! El sabor es auténtico y la porción es generosa. Mi familia y yo venimos cada fin de semana.', img: 'portrait of a happy hispanic woman in her 40s smiling warm lighting casual attire simple neutral background professional headshot' },
                { name: 'Carlos Mendez', location: 'Fort Worth, TX', rating: 5, text: 'La barbacoa está increíble, súper tierna y con un sabor espectacular. El servicio es excelente y los precios muy accesibles. ¡100% recomendado!', img: 'portrait of a friendly hispanic man in his 30s smiling warm lighting casual shirt simple neutral background professional headshot' },
                { name: 'Jennifer Smith', location: 'Dallas, TX', rating: 5, text: 'I discovered this place through a friend and now I\'m hooked! The chicken tinga tacos are amazing. Best Mexican food in the area, hands down!', img: 'portrait of a cheerful young woman blonde hair smiling warm lighting casual attire simple neutral background professional headshot' },
                { name: 'Roberto Hernández', location: 'Grand Prairie, TX', rating: 5, text: 'Pedí el catering para la fiesta de cumpleaños de mi hijo y todos quedaron encantados. La comida llegó caliente y deliciosa. ¡Volveré a ordenar!', img: 'portrait of a middle aged hispanic man with mustache smiling warm lighting casual attire simple neutral background professional headshot' },
                { name: 'Lisa Thompson', location: 'Arlington, TX', rating: 5, text: 'The gorditas are to die for! So authentic and flavorful. The staff is always friendly and the restaurant has such a warm, welcoming atmosphere.', img: 'portrait of a smiling woman in her 50s brown hair warm lighting casual elegant attire simple neutral background professional headshot' },
                { name: 'Miguel Ángel Ruiz', location: 'Irving, TX', rating: 5, text: 'Los combos son una ganga increíble. 3 tacos con agua fresca por $9 es un robo. La calidad es de primera y el sabor te transporta a México.', img: 'portrait of a young hispanic man in his 20s smiling warm lighting casual hoodie simple neutral background professional headshot' }
              ].map((testimonial, idx) => (
                <div key={idx} className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 relative overflow-hidden group reveal-item">
                  <div className="absolute top-3 right-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <i className="ri-double-quotes-r text-6xl md:text-8xl text-[#ed262b]"></i>
                  </div>
                  <div className="relative z-10">
                    <div className="flex gap-1 mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#ed262b] text-base"></i>
                      ))}
                    </div>
                    <p className="text-[#666] text-sm font-figtree font-normal leading-relaxed mb-4 italic">&quot;{testimonial.text}&quot;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 md:w-14 md:h-14 rounded-full overflow-hidden shrink-0">
                        <img src={`https://readdy.ai/api/search-image?query=$%7Btestimonial.img%7D&width=100&height=100&seq=testimonial-${idx}&orientation=squarish`} alt={testimonial.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h4 className="text-sm md:text-base font-bold text-[#33241c]">{testimonial.name}</h4>
                        <p className="text-xs font-figtree font-normal text-[#999] flex items-center gap-1">
                          <i className="ri-map-pin-fill"></i>
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10 md:mt-16 reveal-item">
              <p className="text-base md:text-lg font-figtree font-normal text-[#33241c] mb-4 md:mb-6">¿Ya nos visitaste? ¡Déjanos tu opinión!</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                <a href="https://www.google.com/maps/place/Aqu%C3%AD+se+Come/@32.7418,-97.0862,17z" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-[#ed262b] text-white px-6 py-3 rounded-full text-sm font-rubikMono hover:bg-[#c41e24] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-google-fill"></i>
                  Reseña en Google
                </a>
                <a href="https://yelp.com" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto border-2 border-[#ed262b] text-[#ed262b] px-6 py-3 rounded-full text-sm font-rubikMono hover:bg-[#ed262b] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-star-fill"></i>
                  Reseña en Yelp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CONTACT & CATERING ═══ */}
      <section id="contact" className="py-14 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#ed262b] via-[#c41e24] to-[#a01a20]"></div>
          <img src="https://storage.readdy-site.link/project_files/2cce152d-8aea-43d1-89fb-a15f6a60e2e4/7b27dab5-6380-41d7-8171-3384f31604a3_background.png?v=f96f67fc6915467a43d6bdc887a60a89" alt="Mexican Embroidery" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 md:items-stretch">

            {/* Contact */}
            <div className="text-center text-white flex flex-col">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-phone-fill text-4xl md:text-5xl"></i>
              </div>
              <h3 className="text-2xl md:text-4xl font-rubikMono font-bold mb-4 md:mb-6">Contact Us</h3>
              <p className="text-sm md:text-base font-rubik font-bold leading-relaxed max-w-md mx-auto mb-6 md:mb-8">
                OPEN 7 DAYS A WEEK, 11 AM – 11 PM<br />
                <a href="tel:469-716-7232" className="hover:text-[#FFF8DC] transition-colors cursor-pointer">469.716.7232</a><br />
                <a href="https://maps.google.com/?q=300+W+Randol+Mill+Rd+Arlington+TX+76011" target="_blank" rel="noopener noreferrer" className="hover:text-[#FFF8DC] transition-colors cursor-pointer">
                  300 W Randol Mill Rd, Arlington, TX 76011
                </a>
              </p>
              <form
                id="contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d67almnmvg9ih2c79320"
                method="POST"
                className="max-w-md mx-auto w-full flex flex-col"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  fetch('https://readdy.ai/api/form/d67almnmvg9ih2c79320', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams(formData as any).toString()
                  }).then(() => {
                    alert('Message sent successfully!');
                    form.reset();
                  }).catch(() => alert('Failed to send message'));
                }}
              >
                <div className="w-full rounded-lg overflow-hidden mb-3 md:mb-4" style={{ height: 'calc(2 * 46px + 12px)' }}>
                  <iframe
                    title="Aquí se Come Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3355.3!2d-97.1!3d32.74!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e7d2b2b2b2b2b%3A0x0!2s300+W+Randol+Mill+Rd%2C+Arlington%2C+TX+76011!5e0!3m2!1sen!2sus!4v1"
                    width="100%"
                    height="100%"
                    style={{ border: 0, filter: 'saturate(0.8) contrast(1.05)' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <div className="space-y-3 md:space-y-4">
                  <input type="text" name="name" placeholder="Your Name" required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <input type="email" name="email" placeholder="Your Email" required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <input type="tel" name="phone" placeholder="Your Phone Number" className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <textarea name="message" placeholder="Your Message" rows={3} maxLength={500} required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC] resize-none"></textarea>
                </div>
                <button type="submit" className="mt-10 w-full bg-[#FFF8DC] text-[#ed262b] px-8 py-3.5 md:py-4 rounded-full text-sm md:text-base font-rubikMono hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Send Message
                </button>
              </form>
            </div>

            {/* Catering */}
            <div id="catering" className="text-center text-white flex flex-col">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-restaurant-2-fill text-4xl md:text-5xl"></i>
              </div>
              <h3 className="text-2xl md:text-4xl font-rubikMono mb-4 md:mb-6">We Cater!</h3>
              <p className="text-sm md:text-base font-figtree font-normal leading-relaxed max-w-md mx-auto mb-6 md:mb-8">
                Planning an event? Let us bring the authentic flavors of Mexico to your celebration. From small gatherings to large events, we&apos;ve got you covered.
              </p>
              <form
                id="catering-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d67almnmvg9ih2c7932g"
                method="POST"
                className="max-w-md mx-auto flex flex-col flex-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  fetch('https://readdy.ai/api/form/d67almnmvg9ih2c7932g', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams(formData as any).toString()
                  }).then(() => {
                    alert('Catering request sent successfully!');
                    form.reset();
                  }).catch(() => alert('Failed to send request'));
                }}
              >
                <div className="space-y-3 md:space-y-4">
                  <input type="text" name="name" placeholder="Your Name" required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <input type="email" name="email" placeholder="Your Email" required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <input type="tel" name="phone" placeholder="Phone Number" required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <input type="date" name="event-date" required className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <input type="number" name="guest-count" placeholder="Number of Guests" required min="1" className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC]" />
                  <textarea name="details" placeholder="Event Details" rows={3} maxLength={500} className="w-full px-4 py-3 rounded-lg text-sm text-[#33241c] font-figtree font-normal focus:outline-none focus:ring-2 focus:ring-[#FFF8DC] resize-none"></textarea>
                </div>
                <button type="submit" className="mt-10 w-full bg-[#FFF8DC] text-[#ed262b] px-8 py-3.5 md:py-4 rounded-full text-sm md:text-base font-rubikMono hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Request Quote
                </button>
              </form>
            </div>
          </div>
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0" style={{ width: '3px', borderLeft: '3px dotted rgba(255,255,255,0.75)' }}></div>
        </div>
      </section>

      {/* Local Business Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Restaurant",
          "name": "Aquí se Come",
          "image": "https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/d20479ae32bf411ee3def3c20c31d00e.png",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "300 W Randol Mill Rd",
            "addressLocality": "Arlington",
            "addressRegion": "TX",
            "postalCode": "76011",
            "addressCountry": "US"
          },
          "telephone": "+1-469-716-7232",
          "url": "https://aquisecome.com",
          "openingHours": ["Mo-Su 11:00-23:00"],
          "priceRange": "$",
          "servesCuisine": "Mexican",
          "acceptsReservations": "false"
        })}
      </script>

      {/* ═══ FOOTER ═══ */}
      <footer className="bg-[#33241c] py-10 md:py-16 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          {/* Footer grid: 2 cols mobile, 5 cols desktop */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-10 md:mb-12">

            {/* Brand — spans 2 cols on mobile */}
            <div className="col-span-2 md:col-span-1 flex flex-col items-center text-center md:items-start md:text-left">
              <button onClick={() => scrollToSection('home')} className="cursor-pointer mb-3 md:mb-4 block">
                <img src="https://static.readdy.ai/image/59d5662783296ecd671a864c16c846cd/d20479ae32bf411ee3def3c20c31d00e.png" alt="Aquí se Come" className="h-14 md:h-20" />
              </button>
              <p className="text-sm font-rubik font-extrabold text-[#FFF8DC]/70 mb-2 md:mb-4">Flavor Starts Here</p>
              <p className="text-xs font-figtree font-normal text-[#FFF8DC]/60 leading-relaxed">Authentic Mexican cuisine served with love and tradition in Arlington, Texas.</p>
            </div>

            {/* Menu — col 1 row 2 on mobile */}
            <div>
              <h4 className="text-xs uppercase text-[#ed262b] font-bold tracking-wider mb-3 md:mb-4 font-figtree">Menu</h4>
              <div className="space-y-2 md:space-y-3">
                <button onClick={() => scrollToSection('menu')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">Guisados</button>
                <button onClick={() => scrollToSection('menu')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">Tacos</button>
                <button onClick={() => scrollToSection('combos')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">Combos</button>
                <button onClick={() => scrollToSection('combos')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">Specials</button>
              </div>
            </div>

            {/* Company — col 2 row 2 on mobile */}
            <div>
              <h4 className="text-xs uppercase text-[#ed262b] font-bold tracking-wider mb-3 md:mb-4 font-figtree">Company</h4>
              <div className="space-y-2 md:space-y-3">
                <button onClick={() => scrollToSection('about')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">About Us</button>
                <button onClick={() => scrollToSection('contact')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">Contact</button>
                <button onClick={() => scrollToSection('catering')} className="block text-xs md:text-sm text-[#FFF8DC]/80 hover:text-[#ed262b] hover:underline transition-colors cursor-pointer font-rubikMono">Catering</button>
              </div>
            </div>

            {/* Visit Us — col 1 row 3 on mobile */}
            <div>
              <h4 className="text-xs uppercase text-[#ed262b] font-bold tracking-wider mb-3 md:mb-4 font-figtree">Visit Us</h4>
              <div className="space-y-2">
                <p className="text-xs font-figtree text-[#FFF8DC]/80 leading-relaxed">
                  <a href="https://maps.google.com/?q=300+W+Randol+Mill+Rd+Arlington+TX+76011" target="_blank" rel="noopener noreferrer" className="hover:text-[#ed262b] transition-colors cursor-pointer">
                    300 W Randol Mill Rd<br />Arlington, TX 76011
                  </a>
                </p>
                <p className="text-xs font-figtree text-[#FFF8DC]/80">
                  <a href="tel:469-716-7232" className="hover:text-[#ed262b] transition-colors cursor-pointer">469.716.7232</a>
                </p>
                <p className="text-xs font-rubik font-extrabold text-[#FFF8DC]/60 leading-relaxed pt-1 uppercase">
                  Open 7 days a week<br />11 AM – 11 PM
                </p>
              </div>
            </div>

            {/* Connect — col 2 row 3 on mobile (left-aligns with Company) */}
            <div>
              <h4 className="text-xs uppercase text-[#ed262b] font-bold tracking-wider mb-3 md:mb-4 font-figtree">Connect</h4>
              <div className="flex flex-row md:flex-col gap-3">
                <a href="https://www.facebook.com/p/Aqui-Se-Come-61585768157603/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/aquisecome817/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="mailto:aquisecome-817@hotmail.com" className="w-8 h-8 flex items-center justify-center text-[#FFF8DC] hover:text-[#ed262b] transition-colors cursor-pointer">
                  <i className="ri-mail-fill text-xl"></i>
                </a>
              </div>
            </div>

          </div>

          <div className="border-t border-[#ed262b]/40 pt-6 md:pt-8">
            {/* Mobile footer bottom */}
            <div className="flex flex-col items-center gap-4 md:hidden">
              <p className="text-xs text-[#FFF8DC]/50 text-center font-figtree">&copy; 2025 Aquí se Come. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-[#FFF8DC]/30 hover:text-[#ed262b] transition-colors cursor-pointer font-figtree">
                Powered by Readdy
              </a>
            </div>

            {/* Desktop footer bottom */}
            <div className="hidden md:flex justify-between items-center text-xs text-[#FFF8DC]/50 font-figtree">
              <p>&copy; 2025 Aquí se Come. All rights reserved.</p>
              <div className="flex items-center gap-4">
                <a href="#" className="hover:text-[#ed262b] transition-colors cursor-pointer font-figtree">Privacy Policy</a>
                <span>|</span>
                <a href="#" className="hover:text-[#ed262b] transition-colors cursor-pointer font-figtree">Terms of Service</a>
              </div>
            </div>
            <div className="hidden md:block text-center mt-6">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-[#FFF8DC]/30 hover:text-[#ed262b] transition-colors cursor-pointer font-figtree">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
